import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Table,
  Form,
  Spinner,
  Alert,
  InputGroup,
  Pagination,
} from 'react-bootstrap';
import {
  FaSearch,
  FaArrowLeft,
  FaBalanceScale,
  FaCalendarAlt,
  FaRupeeSign,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const TrialBalance = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [trialBalance, setTrialBalance] = useState([]);
  const [filter, setFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  const fetchTrialBalance = async (page = 1) => {
    if (!fromDate || !toDate) {
      setAlert({
        show: true,
        message: 'Please select both from and to dates.',
        variant: 'warning',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}trial-balance`,
        {
          from_date: fromDate,
          to_date: toDate,
          filter: filter,
          page: page,
          per_page: perPage,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setTrialBalance(response.data.trial_balance || []);
        setCurrentPage(response.data.page || 1);
        setTotalPages(response.data.total_pages || 0);
        setTotalRecords(response.data.total_records || 0);
        if (response.data.trial_balance.length === 0) {
          setAlert({
            show: true,
            message: 'No data found for the selected date range.',
            variant: 'info',
          });
          setTimeout(
            () => setAlert({ show: false, message: '', variant: '' }),
            3000,
          );
        }
      }
    } catch (err) {
      console.error('Trial Balance Error:', err);
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to fetch trial balance. Please try again.',
        variant: 'danger',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
    }
    setLoading(false);
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalOpeningBalance = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    let totalClosingBalance = 0;

    trialBalance.forEach((item) => {
      const openingBalance = parseFloat(item.opening_balance || 0);
      const debit = parseFloat(item.total_debit || 0);
      const credit = parseFloat(item.total_credit || 0);
      const closingBalance = openingBalance + debit - credit;

      totalOpeningBalance += openingBalance;
      totalDebit += debit;
      totalCredit += credit;
      totalClosingBalance += closingBalance;
    });

    return {
      totalOpeningBalance,
      totalDebit,
      totalCredit,
      totalClosingBalance,
    };
  };

  const totals = trialBalance.length > 0 ? calculateTotals() : null;

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaBalanceScale style={{ fontSize: '1.8rem' }} />
                Trial Balance
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                View trial balance for a specific date range
              </p>
            </div>
            <Button
              variant="outline-light"
              onClick={() => navigate(-1)}
              style={{
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaArrowLeft /> Back
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Filter Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div className="row g-3">
            <div className="col-12 col-md-3">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                From Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                    height: '42px',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-3">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                To Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                    height: '42px',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-3">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                Filter
              </Form.Label>
              <Form.Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  borderRadius: '8px',
                  padding: '10px 12px',
                  height: '42px',
                }}
              >
                <option value="All">All</option>
                <option value="Student Only">Student Only</option>
                <option value="Staff Only">Staff Only</option>
                <option value="Others Only">Others Only</option>
              </Form.Select>
            </div>
            <div className="col-12 col-md-3">
              <Form.Label style={{ fontWeight: '600', color: 'transparent' }}>
                Action
              </Form.Label>
              <Button
                onClick={() => fetchTrialBalance(1)}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  height: '42px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FaSearch /> Get Trial Balance
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Trial Balance Table Card */}
        {trialBalance.length > 0 && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <h6
              style={{
                fontWeight: '600',
                marginBottom: '20px',
                color: '#495057',
              }}
            >
              Trial Balance Report
            </h6>
            <div style={{ overflowX: 'auto' }}>
              <Table hover responsive style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                      }}
                    >
                      Account Name
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Opening Balance
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Debit
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Credit
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'right',
                      }}
                    >
                      Closing Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {trialBalance.map((item, index) => {
                    const openingBalance = parseFloat(
                      item.opening_balance || 0,
                    );
                    const debit = parseFloat(item.total_debit || 0);
                    const credit = parseFloat(item.total_credit || 0);
                    const closingBalance = openingBalance + debit - credit;

                    return (
                      <tr
                        key={item.id || index}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                          }}
                        >
                          {item.name}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            fontWeight: '500',
                            color: openingBalance >= 0 ? '#495057' : '#dc3545',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {Math.abs(openingBalance).toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            color: '#28a745',
                            fontWeight: '500',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {debit.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            color: '#dc3545',
                            fontWeight: '500',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {credit.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: closingBalance >= 0 ? '#28a745' : '#dc3545',
                          }}
                        >
                          <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                          {Math.abs(closingBalance).toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  {totals && (
                    <tr
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderTop: '2px solid #dee2e6',
                        fontWeight: '700',
                      }}
                    >
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.95rem',
                          fontWeight: '700',
                        }}
                      >
                        Total
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.95rem',
                          textAlign: 'right',
                          fontWeight: '700',
                          color:
                            totals.totalOpeningBalance >= 0
                              ? '#495057'
                              : '#dc3545',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                        {Math.abs(totals.totalOpeningBalance).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.95rem',
                          textAlign: 'right',
                          color: '#28a745',
                          fontWeight: '700',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                        {totals.totalDebit.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.95rem',
                          textAlign: 'right',
                          color: '#dc3545',
                          fontWeight: '700',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                        {totals.totalCredit.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '0.95rem',
                          textAlign: 'right',
                          fontWeight: '700',
                          color:
                            totals.totalClosingBalance >= 0
                              ? '#28a745'
                              : '#dc3545',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '0.8rem' }} />
                        {Math.abs(totals.totalClosingBalance).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '20px',
                  gap: '15px',
                }}
              >
                <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                  Showing page {currentPage} of {totalPages} ({totalRecords}{' '}
                  total records)
                </div>
                <Pagination style={{ margin: 0 }}>
                  <Pagination.First
                    onClick={() => fetchTrialBalance(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => fetchTrialBalance(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => fetchTrialBalance(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    } else if (
                      page === currentPage - 3 ||
                      page === currentPage + 3
                    ) {
                      return <Pagination.Ellipsis key={page} disabled />;
                    }
                    return null;
                  })}
                  <Pagination.Next
                    onClick={() => fetchTrialBalance(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => fetchTrialBalance(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrialBalance;
